import { Pagination as AntPagination } from 'antd'
import { Select } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import {
  ChevronsLeft,
  ChevronsRight,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import { cn } from '@/utils/cn'

export interface PaginationProps {
  page: number
  pageCount: number
  pageSize: number
  totalCount: number
  pageSizeOptions?: number[]
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
  className?: string
}

function Cell({
  children,
  active,
  disabled,
}: {
  children: React.ReactNode
  active?: boolean
  disabled?: boolean
}) {
  return (
    <span
      className={cn(
        'inline-flex h-8 w-8 select-none items-center justify-center rounded-full text-sm leading-none transition-colors',
        active
          ? 'font-medium text-white border border-transparent'
          : 'border border-solid border-gray-400 bg-white text-gray-700 hover:border-[#CE7A58] hover:text-[#CE7A58] cursor-pointer',
        disabled && 'cursor-not-allowed opacity-40 hover:border-gray-400 hover:text-gray-700',
      )}
      style={active ? { backgroundColor: '#CE7A58' } : undefined}
    >
      {children}
    </span>
  )
}

export function Pagination({
  page,
  pageCount,
  pageSize,
  totalCount,
  pageSizeOptions = [10, 20, 50, 100, 500],
  onPageChange,
  onPageSizeChange,
  className,
}: PaginationProps) {
  const startRecord = totalCount === 0 ? 0 : (page - 1) * pageSize + 1
  const endRecord = Math.min(page * pageSize, totalCount)

  const lastPage = Math.max(pageCount, 1)
  const isFirst = page <= 1
  const isLast = page >= lastPage

  return (
    <div
      className={cn(
        'flex w-full flex-col items-center justify-between gap-3 py-2 sm:flex-row sm:justify-end sm:gap-4',
        '[&_.ant-pagination-item]:m-0 [&_.ant-pagination-item]:h-8 [&_.ant-pagination-item]:min-w-0 [&_.ant-pagination-item]:border-0 [&_.ant-pagination-item]:!bg-transparent',
        '[&_.ant-pagination-item-active]:!bg-transparent',
        '[&_.ant-pagination-prev]:m-0 [&_.ant-pagination-prev]:h-8 [&_.ant-pagination-prev]:min-w-0',
        '[&_.ant-pagination-next]:m-0 [&_.ant-pagination-next]:h-8 [&_.ant-pagination-next]:min-w-0',
        '[&_.ant-pagination-jump-prev]:!hidden [&_.ant-pagination-jump-next]:!hidden [&_.ant-pagination-options]:!hidden',
        '[&_.ant-pagination]:flex [&_.ant-pagination]:items-center [&_.ant-pagination]:gap-1 [&_.ant-pagination]:m-0 [&_.ant-pagination]:p-0',
        className,
      )}
    >
      <div className="flex w-full items-center justify-between sm:w-auto sm:justify-start gap-3">
        <Select
          value={pageSize}
          onValueChange={onPageSizeChange}
          size="medium"
          className="w-20"
          options={pageSizeOptions.map((s) => ({ value: s, label: String(s) }))}
        />
        <span className="text-xs text-[#1E2F41] sm:text-sm truncate">
          {totalCount === 0
            ? 'Không có bản ghi'
            : `Từ ${startRecord} đến ${endRecord} bản ghi`}
        </span>
      </div>

      <div className="flex w-full items-center justify-center gap-1 sm:w-auto sm:justify-end">
        <Button
          variant="outline"
          size="icon"
          disabled={isFirst}
          onClick={() => onPageChange(1)}
        >
          <ChevronsLeft size={16} />
        </Button>

        <AntPagination
          current={page}
          pageSize={pageSize}
          total={totalCount}
          onChange={onPageChange}
          size="small"
          showSizeChanger={false}
          responsive={true}
          itemRender={(pageNum, type, originalElement) => {
            if (type === 'prev') {
              return (
                <Cell disabled={isFirst}>
                  <ChevronLeft size={16} />
                </Cell>
              )
            }
            if (type === 'next') {
              return (
                <Cell disabled={isLast}>
                  <ChevronRight size={16} />
                </Cell>
              )
            }
            if (type === 'page') {
              return <Cell active={pageNum === page}>{pageNum}</Cell>
            }
            return originalElement
          }}
        />

        <Button
          variant="outline"
          size="icon"
          disabled={isLast}
          onClick={() => onPageChange(lastPage)}
        >
          <ChevronsRight size={16} />
        </Button>
      </div>
    </div>
  )
}